import { useEffect, useState } from "react";

export type FontGroup = "default" | "premium";

const FONT_GROUP_KEY = "pulse.fontGroup";

const FONT_GROUPS: FontGroup[] = ["default", "premium"];

function parseFontGroup(value: string | null): FontGroup | null {
  if (value && FONT_GROUPS.includes(value as FontGroup)) return value as FontGroup;
  return null;
}

export function getStoredFontGroup(): FontGroup {
  if (typeof window === "undefined") return "default";
  return parseFontGroup(localStorage.getItem(FONT_GROUP_KEY)) ?? "default";
}

export function applyFontGroup(group: FontGroup) {
  document.documentElement.setAttribute("data-font-group", group);
  localStorage.setItem(FONT_GROUP_KEY, group);
}

export function initFontGroup() {
  applyFontGroup(getStoredFontGroup());
}

/** Live font group from `data-font-group` (settings, init, cross-tab). */
export function useFontGroup(): FontGroup {
  const [group, setGroup] = useState<FontGroup>(() => getStoredFontGroup());

  useEffect(() => {
    const root = document.documentElement;

    const sync = () => {
      const attr = root.getAttribute("data-font-group");
      const next = parseFontGroup(attr) ?? getStoredFontGroup();
      setGroup(next);
    };

    sync();

    const observer = new MutationObserver(sync);
    observer.observe(root, { attributes: true, attributeFilter: ["data-font-group"] });

    const onStorage = (event: StorageEvent) => {
      if (event.key === FONT_GROUP_KEY) sync();
    };
    window.addEventListener("storage", onStorage);

    return () => {
      observer.disconnect();
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  return group;
}
